import { useState, useMemo, useCallback } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Switch } from "../../../components/Switch";
import Button from "../../../components/Button";
import { Tooltip } from "../../../components/tooltip";
import {
  useMetasearchStatus,
  useToggleFeed,
  useDiagnostics,
  useBrands,
} from "../hooks";
import { FeedStatus, Brand, DiagnosticsHotelResult } from "../types";
import {
  extractId,
  formatDateTime,
  getThresholdStyle,
  addToSet,
  removeFromSet,
} from "../utils";
import { Badge, BooleanBadge } from "./StatusBadge";
import { TableSkeleton } from "./TableSkeleton";

const columnHelper = createColumnHelper<FeedStatus>();

const BRAND_ICON_STYLES: Record<string, { label: string; className: string }> = {
  APPROVED: { label: "Approved", className: "bg-green-100 text-green-800 border-green-300" },
  NEW: { label: "Pending", className: "bg-blue-100 text-blue-800 border-blue-300" },
  REVIEW_STATE_NEW: { label: "Pending", className: "bg-blue-100 text-blue-800 border-blue-300" },
  REJECTED: { label: "Rejected", className: "bg-red-100 text-red-800 border-red-300" },
};

function BrandIconBadge({ brand }: { brand: Brand | undefined }) {
  if (!brand)
    return (
      <Badge className="bg-gray-100 text-gray-500 border-gray-300">
        None
      </Badge>
    );
  const state = brand.iconState;
  if (!state)
    return (
      <Badge className="bg-gray-100 text-gray-500 border-gray-300">
        No icon
      </Badge>
    );
  const style = BRAND_ICON_STYLES[state] || {
    label: state,
    className: "bg-gray-100 text-gray-800 border-gray-300",
  };
  return <Badge className={style.className}>{style.label}</Badge>;
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: string;
}) {
  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-[#E9E9EB]">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-2xl font-semibold ${accent || ""}`}>{value}</p>
    </div>
  );
}

function buildDiagnosticsMap(
  diagnostics: DiagnosticsHotelResult[] | undefined,
): Map<string, DiagnosticsHotelResult> {
  const map = new Map<string, DiagnosticsHotelResult>();
  if (!diagnostics) return map;
  for (const h of diagnostics) {
    map.set(h.googlePropertyId, h);
  }
  return map;
}

function buildBrandMap(brands: Brand[] | undefined): Map<string, Brand> {
  const map = new Map<string, Brand>();
  if (!brands) return map;
  for (const b of brands) {
    map.set(extractId(b.name), b);
  }
  return map;
}

export function DashboardContent({
  onViewConfig,
  onAddHotel,
}: {
  onViewConfig: (configId: string) => void;
  onAddHotel?: () => void;
}) {
  const { data: statusData, isLoading, isError } = useMetasearchStatus();
  const { data: diagnosticsData } = useDiagnostics();
  const { data: brandsData } = useBrands();
  const toggleFeed = useToggleFeed();
  const [pendingToggles, setPendingToggles] = useState<Set<string>>(new Set());

  const handleToggle = useCallback(
    (configId: string, currentEnabled: boolean) => {
      if (pendingToggles.has(configId)) return;
      addToSet(setPendingToggles, configId);
      toggleFeed.mutate(
        { configId, enabled: !currentEnabled },
        { onSettled: () => removeFromSet(setPendingToggles, configId) },
      );
    },
    [toggleFeed, pendingToggles],
  );

  const hotels: FeedStatus[] = useMemo(
    () =>
      [...(statusData || [])].sort((a, b) =>
        a.hotelName.localeCompare(b.hotelName),
      ),
    [statusData],
  );

  const latestDiagnostics = useMemo(() => {
    const snapshots = Array.isArray(diagnosticsData) ? diagnosticsData : [];
    return snapshots[0]?.hotelResults;
  }, [diagnosticsData]);

  const diagnosticsMap = useMemo(
    () => buildDiagnosticsMap(latestDiagnostics),
    [latestDiagnostics],
  );
  const brandMap = useMemo(() => buildBrandMap(brandsData), [brandsData]);

  const liveCount = hotels.filter(
    (h) => diagnosticsMap.get(h.googlePropertyId)?.liveOnGoogle === true,
  ).length;
  const brandApprovedCount = hotels.filter((h) => {
    if (!h.brandId) return false;
    const brand = brandMap.get(h.brandId);
    return brand?.iconState === "APPROVED";
  }).length;

  const columns = useMemo(
    () => [
      columnHelper.accessor("hotelName", {
        header: "Hotel",
        cell: (info) => (
          <button
            onClick={() => onViewConfig(info.row.original.configId)}
            className="font-medium text-primary hover:underline text-left"
          >
            {info.getValue()}
          </button>
        ),
      }),
      columnHelper.display({
        id: "googleLive",
        header: () => (
          <div className="flex items-center gap-1.5">
            <span>Google Live</span>
            <Tooltip id="col-google-live" className="!w-[220px]">
              <p className="text-xs">
                Whether this hotel is appearing in Google search results. Updated
                by the diagnostics job.
              </p>
            </Tooltip>
          </div>
        ),
        cell: (info) => {
          const { hotelListSynced, feedEnabled, googlePropertyId } =
            info.row.original;
          if (!hotelListSynced)
            return (
              <Badge className="bg-gray-100 text-gray-500 border-gray-300">
                Not Synced
              </Badge>
            );
          if (!feedEnabled)
            return (
              <Badge className="bg-gray-100 text-gray-500 border-gray-300">
                Feed Off
              </Badge>
            );
          const diag = diagnosticsMap.get(googlePropertyId);
          if (!diag)
            return (
              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                Awaiting Data
              </Badge>
            );
          return <BooleanBadge value={diag.liveOnGoogle} trueLabel="Live" falseLabel="Not Live" />;
        },
      }),
      columnHelper.display({
        id: "brandIcon",
        header: () => (
          <div className="flex items-center gap-1.5">
            <span>Brand Live</span>
            <Tooltip id="col-brand-live" className="!w-[220px]">
              <p className="text-xs">
                Google approval status of the brand icon assigned to this hotel.
              </p>
            </Tooltip>
          </div>
        ),
        cell: (info) => {
          const { brandId } = info.row.original;
          const brand = brandId ? brandMap.get(brandId) : undefined;
          return <BrandIconBadge brand={brand} />;
        },
      }),
      columnHelper.display({
        id: "participation",
        header: () => (
          <div className="flex items-center gap-1.5">
            <span>Participation</span>
            <Tooltip id="col-participation" className="!w-[220px]">
              <p className="text-xs">
                Percentage of traveler searches where this hotel showed a price.
                Higher is better.
              </p>
            </Tooltip>
          </div>
        ),
        cell: (info) => {
          const diag = diagnosticsMap.get(
            info.row.original.googlePropertyId,
          );
          if (!diag || diag.participationPercent == null)
            return (
              <Badge className="bg-gray-100 text-gray-500 border-gray-300">
                N/A
              </Badge>
            );
          return (
            <Badge className={getThresholdStyle(diag.participationPercent)}>
              {diag.participationPercent.toFixed(1)}%
            </Badge>
          );
        },
      }),
      columnHelper.accessor("feedEnabled", {
        header: () => (
          <div className="flex items-center gap-1.5">
            <span>Active</span>
            <Tooltip id="col-active" className="!w-[220px]">
              <p className="text-xs">
                Toggle to enable or disable sending rate and availability data to
                Google.
              </p>
            </Tooltip>
          </div>
        ),
        cell: (info) => {
          const isPending = pendingToggles.has(info.row.original.configId);
          return (
            <div className={isPending ? "opacity-50 pointer-events-none" : ""}>
              <Switch
                checked={info.getValue()}
                onChange={() =>
                  handleToggle(info.row.original.configId, info.getValue())
                }
              />
            </div>
          );
        },
      }),
      columnHelper.accessor("lastAriSuccess", {
        header: () => (
          <div className="flex items-center gap-1.5">
            <span>Last Synced</span>
            <Tooltip id="col-last-synced" className="!w-[220px]">
              <p className="text-xs">
                When rates and availability were last successfully sent to
                Google.
              </p>
            </Tooltip>
          </div>
        ),
        cell: (info) => (
          <span className="text-gray-500 text-xs">
            {formatDateTime(info.getValue())}
          </span>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        cell: (info) => (
          <div className="flex items-center justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewConfig(info.row.original.configId)}
            >
              Details
            </Button>
          </div>
        ),
      }),
    ],
    [
      pendingToggles,
      handleToggle,
      onViewConfig,
      diagnosticsMap,
      brandMap,
    ],
  );

  const table = useReactTable({
    data: hotels,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isError) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-12 text-center">
        <h3 className="text-lg font-semibold text-red-600 mb-1">
          Failed to load dashboard
        </h3>
        <p className="text-sm text-gray-400">
          Please try refreshing the page.
        </p>
      </div>
    );
  }

  if (!isLoading && hotels.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-12 text-center">
        <div className="text-4xl mb-3 text-gray-300">⌂</div>
        <h3 className="text-lg font-semibold text-gray-700 mb-1">
          No hotels onboarded yet
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          Add your first hotel to Google Metasearch.
        </p>
        <Button onClick={onAddHotel}>+ New Hotel</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Hotels" value={hotels.length} />
        <StatCard
          label="Live on Google"
          value={liveCount}
          accent="text-green-600"
        />
        <StatCard
          label="Brand Approved"
          value={brandApprovedCount}
          accent="text-green-600"
        />
        <StatCard
          label="Active"
          value={hotels.filter((h) => h.feedEnabled).length}
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] overflow-auto">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, idx) => (
                  <th
                    key={header.id}
                    className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-[#F4F4F6] ${
                      idx === 0 ? "rounded-tl-lg" : ""
                    } ${
                      idx === headerGroup.headers.length - 1
                        ? "rounded-tr-lg"
                        : ""
                    }`}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              <TableSkeleton rows={4} columns={columns.length} />
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-3.5 border-b border-b-[#E9E9EB] text-sm"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
